import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';
import { useAuth } from '@/shared/lib/auth';
import {
  maskBirthday,
  maskBirthdayFromStored,
  maskCpf,
  maskPhone,
  maskPhoneFromStored,
} from '@/shared/lib/input-masks';
import {
  formatUniqueIdForDisplay,
  isCpfUniqueIdLabel,
  normalizeUniqueIdAsString,
} from '@/shared/lib/unique-id-lookup';
import type { Lead } from '@/shared/types';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Tooltip } from '@/shared/ui/tooltip';
import { MaskedInput } from '@/shared/components/masked-input';
import { saveLead } from '../api/use-leads';

const schema = z.object({
  uniqueId: z.string().optional(),
  email: z.string().email('E-mail inválido').optional().or(z.literal('')),
  phone: z.string().optional(),
  birthday: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

interface LeadEditDialogProps {
  lead: Lead | null;
  idPassbook: string;
  uniqueIdLabel?: string;
  onClose: () => void;
  onSaved: () => void;
}

export function LeadEditDialog({ lead, idPassbook, uniqueIdLabel = 'CPF', onClose, onSaved }: LeadEditDialogProps) {
  const { user } = useAuth();
  // A marca (Client) logada não pode editar CPF nem aniversário; o Admin (inspeção) pode.
  const brandLocked = user?.role === 'Client';
  const form = useForm<FormValues>({ resolver: zodResolver(schema) });
  const uidIsCpf = isCpfUniqueIdLabel(uniqueIdLabel);

  useEffect(() => {
    if (lead) {
      form.reset({
        uniqueId: formatUniqueIdForDisplay(lead.profile?.uniqueId, uniqueIdLabel),
        email: lead.email ?? '',
        phone: maskPhoneFromStored(lead.phone),
        birthday: maskBirthdayFromStored(lead.birthday),
      });
    }
  }, [lead, form, uniqueIdLabel]);

  const onSubmit = form.handleSubmit(async (values) => {
    if (!lead?.serialNumber) {
      toast.error('Lead incompleto — sem serial do cartão.');
      return;
    }
    try {
      await saveLead(idPassbook, {
        _id: lead._id,
        serialNumber: lead.serialNumber,
        profile: {
          name: lead.profile?.name,
          uniqueId: brandLocked
            ? normalizeUniqueIdAsString(lead.profile?.uniqueId)
            : values.uniqueId,
        },
        email: values.email || undefined,
        phone: values.phone || undefined,
        birthday: brandLocked ? lead.birthday : values.birthday || undefined,
        gender: lead.gender,
        observacao: lead.observacao,
        total_points: lead.total_points,
        description: '',
      });
      toast.success('Lead atualizado.');
      onClose();
      onSaved();
    } catch {
      toast.error('Falha ao salvar.');
    }
  });

  return (
    <Dialog open={Boolean(lead)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar lead</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="flex items-center gap-1.5">
              Nome
              <Tooltip content="O nome não pode ser alterado por aqui" side="top">
                <Lock className="size-3 text-muted-foreground" />
              </Tooltip>
            </Label>
            <Input id="name" value={lead?.profile?.name ?? ''} readOnly disabled />
          </div>
          <div className="space-y-2">
            <Label htmlFor="uid" className="flex items-center gap-1.5">
              {uniqueIdLabel}
              {brandLocked ? (
                <Tooltip content="Apenas a Fixe pode alterar este campo" side="top">
                  <Lock className="size-3 text-muted-foreground" />
                </Tooltip>
              ) : null}
            </Label>
            <Controller
              name="uniqueId"
              control={form.control}
              render={({ field }) =>
                uidIsCpf ? (
                  <MaskedInput
                    id="uid"
                    disabled={brandLocked}
                    value={field.value ?? ''}
                    onValueChange={field.onChange}
                    onBlur={field.onBlur}
                    mask={maskCpf}
                    inputMode="numeric"
                    placeholder="000.000.000-00"
                  />
                ) : (
                  <Input
                    id="uid"
                    disabled={brandLocked}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder={uniqueIdLabel}
                  />
                )
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">E-mail</Label>
            <Input id="email" type="email" {...form.register('email')} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Telefone</Label>
            <Controller
              name="phone"
              control={form.control}
              render={({ field }) => (
                <MaskedInput
                  id="phone"
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  mask={maskPhone}
                  inputMode="tel"
                  placeholder="(11) 99999-9999"
                />
              )}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="birthday" className="flex items-center gap-1.5">
              Aniversário
              {brandLocked ? (
                <Tooltip content="Apenas a Fixe pode alterar este campo" side="top">
                  <Lock className="size-3 text-muted-foreground" />
                </Tooltip>
              ) : null}
            </Label>
            <Controller
              name="birthday"
              control={form.control}
              render={({ field }) => (
                <MaskedInput
                  id="birthday"
                  disabled={brandLocked}
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  mask={maskBirthday}
                  inputMode="numeric"
                  placeholder="DD/MM/AAAA"
                />
              )}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>Cancelar</Button>
            <Button type="submit" loading={form.formState.isSubmitting}>Salvar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
